import { isSameDay, isBefore, differenceInMinutes } from 'date-fns';

import type { Customer, User } from 'types/Global';
import type { Message } from 'types/Messages';

const TIME_FRAME = 3; // as minutes
export const isCreatedByMyself = (message: Message) => message.createdBy?.type === 'SYSTEM' && message.createdBy?.onCustomerBehalf;
export const canDisplayMessage = (message: Message) => message.visibility === 'PUBLIC';

const isSameWriter = (message: Message, otherMessage: Message): boolean => {
  if (message && otherMessage) {
    return message.createdBy.displayName === otherMessage.createdBy.displayName;
  }
  return false;
};

const isInSameTimeFrame = (message: Message, otherMessage: Message): boolean => {
  if (message && otherMessage) {
    const difference = Math.abs(differenceInMinutes(otherMessage.sentOn, message.sentOn, { roundingMethod: 'ceil' }));
    return difference <= TIME_FRAME;
  }
  return false;
};

const isSameChannel = (message: Message, otherMessage: Message): boolean => {
  if (message && otherMessage) {
    return message?.channel === otherMessage?.channel;
  }
  return false;
};

const isSameDate = (message: Message, otherMessage: Message): boolean => {
  if (message && otherMessage) {
    return isSameDay(message.sentOn, otherMessage.sentOn);
  }
  return false;
};

/** Group messages by timeframe, channel and writer */
const groupMessages = (messages: Array<Message>, date: string): Array<Array<Message>> =>
  messages.reduce<Array<Array<Message>>>((acc, message, index) => {
    const sameDate = isSameDay(date, message.sentOn);
    if (!sameDate) return acc;

    const previous = messages?.[index - 1];
    const sameTimeframe = isInSameTimeFrame(message, previous);
    const sameChannel = isSameChannel(message, previous);
    const sameWriter = isSameWriter(message, previous);
    if (sameChannel && sameTimeframe && sameWriter) {
      acc[acc.length - 1].push(message);
    } else {
      acc.push([message]);
    }
    return acc;
  }, []);

export type GroupedMessages = {
  date: string;
  createdBy: User;
  customer?: Customer;
  messages: Array<Array<Message>>;
};

/** Group by date then each date has a list of messages by timeframe, channel and writer */
export const groupMessagesByDate = (messages: Array<Message>): Array<GroupedMessages> =>
  messages.reduce<Array<GroupedMessages>>((acc, message, index) => {
    // By date
    const { createdBy, customer, sentOn: date } = message;
    const previous = messages?.[index - 1];
    const sameDate = isSameDate(message, previous);

    if (!sameDate) {
      acc.push({
        date,
        customer,
        createdBy,
        messages: groupMessages(messages, date),
      });
    }
    return acc;
  }, []);

export const getLastMessageByDate = (messages: Array<Message>): Message | null =>
  messages.reduce<Message | null>((lastMessage, current) => {
    if (!lastMessage || isBefore(lastMessage.sentOn, current.sentOn)) {
      // eslint-disable-next-line no-param-reassign
      lastMessage = current;
    }
    return lastMessage;
  }, null);
